import React, { useState, useEffect, useContext, ReactNode } from "react";

interface AuthProviderProps {
  children?: ReactNode;
}

interface AuthContextProvider {
  isAuthenticated: boolean;
  user: Claim[];
  isLoading: boolean;
  login: Function;
  logout: Function;
  isActive: boolean;
  setLocalAuthenticated: Function;
}

interface Claim {
  type: string;
  value: string;
}

export const AuthContext = React.createContext<AuthContextProvider>({
  isAuthenticated: false,
  user: [],
  isLoading: false,
  login: () => {},
  logout: () => {},
  isActive: false,
  setLocalAuthenticated: (rrn: string) => {},
});
export const useAuth = () => useContext(AuthContext);
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const authUrl = process.env.REACT_APP_AUTH_URL;
  const isActive = (authUrl ?? "").length > 0;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<Claim[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getUser = async () => {
    const response = await fetch(`${authUrl}/auth/getUser`, {
      credentials: "include",
    });
    const json = await response.json();

    setIsAuthenticated(json.isAuthenticated);
    setIsLoading(false);
    if (json.isAuthenticated) {
      setUser(json.claims);
      return;
    }

    const rrn = sessionStorage.getItem("CLBSurveyAppLocalRrn");
    if (rrn) {
      setUser([{ type: "localrrn", value: rrn }]);
      setIsAuthenticated(true);
    }
  };

  useEffect(() => {
    getUser();
  }, []);

  const login = () => {
    window.location.href = `${authUrl}/auth/login?returnurl=${encodeURI(
      process.env.REACT_APP_ENV_URL ?? ""
    )}`;
  };

  const logout = () => {
    if (isAuthenticated && user.find((r) => r.type === "localrrn")) {
      sessionStorage.removeItem("CLBSurveyAppLocalRrn");
      setUser([]);
      setIsAuthenticated(false);
    }

    window.location.href = `${authUrl}/auth/logout?returnurl=${encodeURI(
      process.env.REACT_APP_ENV_URL ?? ""
    )}`;
  };

  const setLocalAuthenticated = (rrn: string) => {
    setUser([{ type: "localrrn", value: rrn }]);
    setIsAuthenticated(true);
    sessionStorage.setItem("CLBSurveyAppLocalRrn", rrn);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        login,
        logout,
        isActive,
        setLocalAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
